import * as colorbrewer from 'colorbrewer';
import { min, max } from 'simple-statistics';

/**
 * @param {string} colorKey the color brewer pallet key
 * @param {number} colorClusterCount the number of colors to return
 * @returns {string[]} a list of colors
 */
export const getColorBrewerPallet = (colorKey, colorClusterCount) => {
  const colors =
    colorbrewer[colorKey || 'Blues'] && colorbrewer[colorKey || 'Blues'][colorClusterCount || 5];
  return colors && [...colors];
};

/**
 * @param {FlexObject} item the View.column or joined View.geometry object
 * @returns {string|string[]}
 */
export const getItemColor = (item) => {
  const { colorType, color, colorClusterCount, colorKey, colors } = item;
  if (colorType === 'solid' || (typeof colorType === 'undefined' && typeof color !== undefined)) {
    return color;
  }

  if (colorType === 'categorical') {
    return colors || getColorBrewerPallet(colorKey, colorClusterCount);
  }

  if (colorType === 'clusters') {
    return colors || getColorBrewerPallet(colorKey, colorClusterCount);
  }
};

/**
 * @param {FlexObject} item the View.column or joined View.geometry object
 * @param {FlexObject} datum the "row" of data for which to return a color
 */
export const getCellColor = (item, datum) => {
  const colors = getItemColor(item);
  const { breaks, transform } = item;

  // Solid color schemes should simply return the color string
  if (typeof colors === 'string') {
    return colors;
  }

  // Linear / Normalized breaks use uniform breaks and always have min/max of 0/100
  if (Array.isArray(colors) && (transform === 'normalize' || transform === 'linear')) {
    let scaleMax = 100;
    const bucketSize = scaleMax / colors.length;
    for (let c = 0; c < colors.length; c = c + 1) {
      const min = c * bucketSize;
      const max = (c + 1) * bucketSize;
      if (datum >= min && datum < max) {
        return colors[c];
      }
    }

    return colors[colors.length - 1];
  }

  if (item.colorType === 'categorical') {
    const colorIndex = item.categories.indexOf(datum);
    if (colorIndex !== -1) {
      return item.colors[colorIndex];
    } else {
      return '#eeeeee';
    }
  }

  // Scalar breaks should be useed to define the color
  if (Array.isArray(colors) && Array.isArray(breaks)) {
    return getColorFromScalarBreaks(colors, breaks, datum);
  }
};

export const getSymbolColor = (item, datum) => {
  const colors = getItemColor(item);
  const { breaks } = item;

  // Solid color schemes should simply return the color string
  if (typeof colors === 'string') {
    return colors;
  }
  // Scalar breaks should be useed to define the color
  if (Array.isArray(colors) && Array.isArray(breaks)) {
    return colors[colors.length - 2];
  }
};

/**
 * @param {FlexObject[]} data the full data for the View
 * @param {string} key the prop name of the View.column property
 * @returns {string[]|number[]} an array of unique values from the data
 */
export const getUniqueValues = (data, key) => [
  ...new Set(
    data.map((d) => {
      if (d[key] === '') {
        return 0;
      } else {
        return d[key];
      }
    })
  ),
];

/**
 * @param {*} item the View.column or joined View.geometry object
 * @param {*} clusterCount the number of clusters
 * @param {*} data the full data for the View
 * @returns {number[]} a list of scalar breaks values
 */
export const getScalarBreaks = (item, clusterCount, data) => {
  const { dataIndex } = item;
  const clusters = clusterCount || 3;
  const values = data.map((d) => d[dataIndex]);
  const scaleMin = min(values);
  const scaleMax = max(values);
  const bucketSize = (scaleMax - scaleMin) / clusters;
  const breaks = [];
  for (let b = 1; b <= clusters; b += 1) {
    const clusterMax = scaleMin + bucketSize * b;
    breaks.push(parseFloat(Math.round(clusterMax * 100) / 100));
  }
  return breaks;
};

/**
 * @param {*} item the View.column or joined View.geometry object
 * @param {*} data the full data for the View
 * @returns {*} a list of scalar breaks values
 */
export const getMinMaxValues = (item, data) => {
  const { dataIndex } = item;
  const values = data.map((d) => d[dataIndex]);
  if (values.length > 0) {
    const scaleMin = min(values);
    const scaleMax = max(values);
    return { scaleMin, scaleMax };
  } else {
    return null;
  }
};

/**
 * @param {string[]} colors the colors from View.column
 * @param {number[]} breaks the scalar breaks from View.column
 * @param {number} value the dutum property being mapped to a color
 * @returns {string} the color for this value
 */
export const getColorFromScalarBreaks = (colors, breaks, value) => {
  if (!value && value !== 0) {
    return null;
  }

  if (typeof value === 'string') {
    // we can modify since JS is a pass by value language with no side effects
    value = value.replace(',', '');
  }

  value = parseFloat(value);

  for (let c = 0; c < colors.length; c += 1) {
    if (value <= breaks[c]) {
      return colors[c];
    }
  }
  return null;
};

/*!
 * Get the contrasting color for any hex color
 * (c) 2019 Chris Ferdinandi, MIT License, https://gomakethings.com
 * Derived from work by Brian Suda, https://24ways.org/2010/calculating-color-contrast/
 * @param  {String} A hexcolor value
 * @return {String} The contrasting color (black or white)
 */
export const getContrast = (hexcolor) => {
  // If a leading # is provided, remove it
  if (hexcolor.slice(0, 1) === '#') {
    hexcolor = hexcolor.slice(1);
  }

  // If a three-character hexcode, make six-character
  if (hexcolor.length === 3) {
    hexcolor = hexcolor
      .split('')
      .map((hex) => hex + hex)
      .join('');
  }

  // Convert to RGB value
  var r = parseInt(hexcolor.substr(0, 2), 16);
  var g = parseInt(hexcolor.substr(2, 2), 16);
  var b = parseInt(hexcolor.substr(4, 2), 16);

  // Get YIQ ratio
  var yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  return yiq >= 140 ? '#111' : '#fff';
};
